import { lazyComponentLoader } from '@terminal/utils/component-loader/componentLoader';
import { AssetOverview } from '@terminal/views/asset-overview/AssetOverview';
import { ForgotPasswordView } from '@terminal/views/auth/forgotPassword';
import OnHold from '@terminal/views/auth/on-hold';
import { ResetPasswordView } from '@terminal/views/auth/reset-password-view/ResetPasswordView';
import { Verify } from '@terminal/views/auth/verify/Verify';
import { CalendarView } from '@terminal/views/calendar/CalendarView';
import { ServerError } from '@terminal/views/error/server-error/ServerError';
import { Scenario } from '@terminal/views/scenario/Scenario';
import { InteractiveBrokersCallback } from '@terminal/views/settings/portfolio-view/components/interactive-brokers-callback/InteractiveBrokersCallback';

import { Redirect } from '../routes/Redirect';
import { News } from '../views/news/News';
import { appPaths, authPaths } from './app-paths';
import { queryKeys } from './query-params';
import { AppRoute } from './Routes';

const createRoutesFromPathArray = (
  paths: string[],
  route: AppRoute
): AppRoute[] => paths.map(path => ({ path, ...route }));

const Dashboard = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "dashboard" */ '../views/dashboard'
    ),
  'dashboard'
);

const Alfred = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "alfred" */ '../views/alfred'
    ),
  'alfred'
);

const AlfredAppConnection = lazyComponentLoader(
  () =>
    import(
      /* webpackChunkName: "alfredAppConnection" */ '../views/alfred/AlfredAppConnection'
    ),
  'alfredAppConnection'
);

const Screener = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "screener" */ '../views/screener'
    ),
  'screener'
);

const TermsOfService = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "termsOfService" */ '../views/terms-of-service'
    ),
  'termsOfService'
);
const Explore = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "explore" */ '../views/explore'
    ),
  'insights'
);

const Onboarding = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "onboarding" */ '../views/onboarding'
    ),
  'onboarding'
);

const TurboChart = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "turboChart" */ '../views/turbo-chart'
    ),
  'TurboChart'
);

const ChartV2View = lazyComponentLoader(
  () => import(/* webpackChunkName: "chartV2" */ '../views/chart-v2'),
  'chartV2'
);

const WatchlistPage = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "dashboard" */ '../views/watchlist-page'
    ),
  'watchlist-page'
);

const Settings = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "settings" */ '@terminal/views/settings'
    ),
  'settings'
);
const SignUp = lazyComponentLoader(
  () => import(/* webpackChunkName: "signUp" */ '@terminal/views/auth/sign-up'),
  'signUp'
);
const LoginView = lazyComponentLoader(
  () =>
    import(
      /* webpackChunkName: "loginView" */ '@terminal/views/auth/login-view'
    ),
  'login-view'
);

const ThemeOverview = lazyComponentLoader(
  () =>
    import(
      /* webpackChunkName: "themeOverview" */ '@terminal/views/theme-overview'
    ),
  'ThemeOverview'
);

const InsightFeed = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "insightFeed" */ '../views/insight-feed'
    ),
  'insightFeed'
);

const KgInsight = lazyComponentLoader(
  () => import(/* webpackChunkName: "kgInsight" */ '../views/kg-insight'),
  'kgInsight'
);

const Alerts = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "alerts" */ '../views/alerts'
    ),
  'alerts'
);

export const routes: AppRoute[] = [
  {
    path: appPaths.base,
    element: <Dashboard />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.dashboard',
    },
  },
  {
    path: `${appPaths.themeOverview}/:themeId`,
    element: <ThemeOverview />,
  },
  {
    path: appPaths.assetOverview,
    element: <AssetOverview />,
  },
  ...createRoutesFromPathArray(
    [appPaths.watchlist, `${appPaths.watchlist}/:watchlistId`],
    { element: <WatchlistPage /> }
  ),
  {
    path: appPaths.chart,
    element: <TurboChart />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.chart',
    },
  },
  {
    path: appPaths.chartV2,
    element: <ChartV2View />,
    featureFlag: 'enable_chartv2',
  },
  {
    path: appPaths.screener,
    element: <Screener />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.screener',
    },
  },
  {
    path: `${appPaths.screener}/:screenerId`,
    element: <Screener />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.screener',
    },
  },
  {
    path: appPaths.news,
    element: <News />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.news',
    },
  },
  {
    path: appPaths.tos,
    element: <TermsOfService />,
  },
  {
    path: appPaths.onboarding,
    element: <Onboarding />,
  },
  {
    path: appPaths.alfred,
    element: <Alfred />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.alfred',
    },
  },

  ...createRoutesFromPathArray(
    [
      appPaths.settings.account,
      appPaths.settings.appearance,
      appPaths.settings.portfolio,
    ],
    { element: <Settings /> }
  ),
  {
    path: `${appPaths.settings.subscription}/*`,
    element: <Settings />,
  },
  {
    path: appPaths.accountOnHold,
    element: <OnHold />,
  },
];

export const authRoutes: AppRoute[] = [
  {
    path: authPaths.login,
    element: <LoginView />,
  },
  {
    path: authPaths.signUp,
    element: <SignUp />,
  },
];

export const commonRoutes: AppRoute[] = [
  {
    path: appPaths.verifyEmail,
    element: <Verify />,
  },
  {
    path: appPaths.error,
    element: <ServerError />,
  },
  ...createRoutesFromPathArray(
    [
      authPaths.forgotPassword,
      `${authPaths.forgotPassword}/:${queryKeys.emailReset}`,
    ],
    {
      element: <ForgotPasswordView />,
    }
  ),
  {
    path: authPaths.resetPassword,
    element: <ResetPasswordView />,
  },
  {
    path: appPaths.ib,
    element: <InteractiveBrokersCallback />,
  },
];

export const nonBasicRoutes: AppRoute[] = [
  {
    path: appPaths.events,
    element: <CalendarView />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.events',
    },
  },
  {
    path: appPaths.scenario,
    element: <Scenario />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.scenario',
    },
  },
];

export const nonBasicOrLightRoutes: AppRoute[] = [
  {
    path: appPaths.insightFeed,
    element: (
      <Redirect
        from={appPaths.insightFeed}
        to={`${appPaths.insightFeed}/focus`}
      />
    ),
  },
  {
    path: `${appPaths.insightFeed}/focus`,
    element: <InsightFeed />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.insightFeed',
    },
  },
  {
    path: `${appPaths.insightFeed}/explore`,
    element: <InsightFeed />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.insightFeed',
    },
  },
  {
    path: appPaths.kgInsight,
    element: <KgInsight />,
    featureFlag: 'catalyst_generation',
  },
  { path: appPaths.alerts, element: <Alerts /> },
];

export const basicOrLightOnlyRoutes: AppRoute[] = [
  {
    path: appPaths.insights,
    element: <Explore />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.insights',
    },
  },
];

export const featureRoutes: AppRoute[] = [
  {
    path: appPaths.alfredAppConnection,
    element: <AlfredAppConnection />,
    featureFlag: 'msf_connection',
  },
];
